import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'

interface PartRow {
  id: string
  name: string
  unit: string
  isActive: boolean
  totalQuantity: number
  stockByPoint: { pointId: string; pointName: string; quantity: number }[]
}

interface MovementRow {
  id: string
  type: 'receipt' | 'transfer' | 'writeoff'
  partName: string
  unit: string
  quantity: number
  unitCost: number
  totalCost: number
  fromPoint: string | null
  toPoint: string | null
  bikeCode: string | null
  note: string | null
  createdBy: string
  createdAt: string
}

const TYPE_LABEL: Record<string, string> = {
  receipt: '📥 Приход',
  transfer: '🔄 Перемещение',
  writeoff: '🔧 Списание',
}

const fmtMoney = (n: number) => n.toLocaleString('ru-RU') + ' ₽'
const fmtDate = (s: string) => new Date(s).toLocaleString('ru', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

export default function WarehousePage() {
  const qc = useQueryClient()
  const isOwner = useAuthStore((s) => s.user?.role) === 'owner'
  const [tab, setTab] = useState<'stock' | 'history' | 'report'>('stock')
  const [showHandOver, setShowHandOver] = useState(false)
  const [moveFrom, setMoveFrom] = useState<MechanicStockRow | null>(null)
  const [showNewPart, setShowNewPart] = useState(false)
  const [showReceipt, setShowReceipt] = useState(false)
  const [showTransfer, setShowTransfer] = useState(false)
  const [showWriteoff, setShowWriteoff] = useState(false)

  const { data: partsData, isLoading } = useQuery<{ parts: PartRow[] }>({
    queryKey: ['warehouse-parts'],
    queryFn: () => api.get('/warehouse/parts').then((r) => r.data),
  })
  const parts = partsData?.parts ?? []

  const { data: movData } = useQuery<{ movements: MovementRow[] }>({
    queryKey: ['warehouse-movements'],
    queryFn: () => api.get('/warehouse/movements', { params: { limit: 100 } }).then((r) => r.data),
    enabled: tab === 'history',
  })
  const movements = movData?.movements ?? []

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['warehouse-parts'] })
    qc.invalidateQueries({ queryKey: ['warehouse-movements'] })
  }

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Склад запчастей</h1>
        <div className="flex flex-wrap gap-2">
          {isOwner && <button onClick={() => setShowReceipt(true)} className="bg-brand-600 text-white text-sm px-3 py-2 rounded-xl font-medium">📥 Приход</button>}
          <button onClick={() => setShowTransfer(true)} disabled={parts.length === 0} className="border border-gray-300 text-sm px-3 py-2 rounded-xl disabled:opacity-50">🔄 Перемещение</button>
          <button onClick={() => setShowWriteoff(true)} disabled={parts.length === 0} className="border border-gray-300 text-sm px-3 py-2 rounded-xl disabled:opacity-50">🔧 Списание</button>
          {isOwner && <button onClick={() => setShowHandOver(true)} disabled={parts.length === 0} className="border border-gray-300 text-sm px-3 py-2 rounded-xl disabled:opacity-50">🧰 Передать механику</button>}
          {isOwner && <button onClick={() => setShowNewPart(true)} className="border border-gray-300 text-sm px-3 py-2 rounded-xl">+ Запчасть</button>}
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-2xl p-1 w-fit">
        <button onClick={() => setTab('stock')} className={`text-sm px-4 py-1.5 rounded-xl ${tab === 'stock' ? 'bg-white shadow-sm font-medium' : 'text-gray-500'}`}>Остатки</button>
        <button onClick={() => setTab('history')} className={`text-sm px-4 py-1.5 rounded-xl ${tab === 'history' ? 'bg-white shadow-sm font-medium' : 'text-gray-500'}`}>История</button>
        <button onClick={() => setTab('report')} className={`text-sm px-4 py-1.5 rounded-xl ${tab === 'report' ? 'bg-white shadow-sm font-medium' : 'text-gray-500'}`}>
          {isOwner ? 'У механиков' : 'Под отчёт'}
        </button>
      </div>

      {tab === 'report' && <UnderReportTab isOwner={isOwner} onMove={setMoveFrom} />}

      {tab === 'stock' && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {isLoading && <p className="text-gray-400 text-center py-8">Загрузка...</p>}
          {!isLoading && parts.length === 0 && (
            <p className="text-gray-400 text-center py-10">Запчастей пока нет. Добавьте через «+ Запчасть» и оформите приход.</p>
          )}
          {parts.map((p) => (
            <div key={p.id} className="border-b border-gray-100 last:border-0 px-4 py-3">
              <div className="flex justify-between items-start">
                <p className="font-medium text-sm">{p.name}</p>
                <p className="text-sm font-bold">{p.totalQuantity} {p.unit}</p>
              </div>
              {p.stockByPoint.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {p.stockByPoint.map((s) => (
                    <span key={s.pointId} className="text-xs text-gray-500 bg-gray-50 rounded-full px-2 py-0.5">
                      {s.pointName}: {s.quantity}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'history' && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {movements.length === 0 && <p className="text-gray-400 text-center py-10">Движений пока нет</p>}
          {movements.map((m) => (
            <div key={m.id} className="border-b border-gray-100 last:border-0 px-4 py-3 text-sm">
              <div className="flex justify-between">
                <span>{TYPE_LABEL[m.type] ?? m.type}</span>
                <span className="text-gray-400 text-xs">{fmtDate(m.createdAt)}</span>
              </div>
              <p className="mt-0.5">
                <strong>{m.partName}</strong> · {m.quantity} {m.unit}
                {m.type === 'receipt' && m.unitCost > 0 && <span className="text-gray-500"> · {fmtMoney(m.totalCost)}</span>}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {m.fromPoint && <>из {m.fromPoint} </>}
                {m.toPoint && <>→ {m.toPoint} </>}
                {m.bikeCode && <>· велик #{m.bikeCode} </>}
                · {m.createdBy}
              </p>
              {m.note && <p className="text-xs text-gray-400 mt-0.5">{m.note}</p>}
            </div>
          ))}
        </div>
      )}

      {showNewPart && <NewPartModal onClose={() => setShowNewPart(false)} onDone={() => { setShowNewPart(false); refresh() }} />}
      {showReceipt && <ReceiptModal parts={parts} onClose={() => setShowReceipt(false)} onDone={() => { setShowReceipt(false); refresh() }} />}
      {showTransfer && <TransferModal parts={parts} onClose={() => setShowTransfer(false)} onDone={() => { setShowTransfer(false); refresh() }} />}
      {showWriteoff && <WriteoffModal parts={parts} onClose={() => setShowWriteoff(false)} onDone={() => { setShowWriteoff(false); refresh() }} />}
      {showHandOver && <HandOverModal parts={parts} onClose={() => setShowHandOver(false)} onDone={() => { setShowHandOver(false); refresh() }} />}
      {moveFrom && <MoveFromMechanicModal row={moveFrom} onClose={() => setMoveFrom(null)} onDone={() => { setMoveFrom(null); refresh() }} />}
    </div>
  )
}

interface MechanicStockRow { partId: string; partName: string; unit: string; mechanicId: string; mechanicName: string; quantity: number }
interface IncomingRow { id: string; partName: string; unit: string; quantity: number; fromPoint: string | null; fromName: string; note?: string | null }

/**
 * «Под отчёт»: механик видит что ему передали (принять/отклонить) и свой остаток;
 * владелец видит остатки всех механиков и может перенести их куда угодно.
 */
function UnderReportTab({ isOwner, onMove }: { isOwner: boolean; onMove: (r: MechanicStockRow) => void }) {
  const qc = useQueryClient()
  const { data: incomingData } = useQuery<{ incoming: IncomingRow[] }>({
    queryKey: ['warehouse-incoming'],
    queryFn: () => api.get('/warehouse/incoming').then((r) => r.data),
    enabled: !isOwner,
  })
  const { data: stockData } = useQuery<{ stock: MechanicStockRow[] }>({
    queryKey: ['mechanic-stock'],
    queryFn: () => api.get('/warehouse/mechanic-stock').then((r) => r.data),
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['warehouse-incoming'] })
    qc.invalidateQueries({ queryKey: ['mechanic-stock'] })
    qc.invalidateQueries({ queryKey: ['warehouse-parts'] })
  }

  const confirm = useMutation({
    mutationFn: (id: string) => api.post(`/warehouse/incoming/${id}/confirm`, {}),
    onSuccess: refresh,
    onError: () => alert('Не удалось подтвердить'),
  })
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/warehouse/incoming/${id}/reject`, { reason }),
    onSuccess: refresh,
    onError: () => alert('Не удалось отклонить'),
  })

  const incoming = incomingData?.incoming ?? []
  const stock = stockData?.stock ?? []

  return (
    <div className="space-y-4">
      {!isOwner && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">Ожидают подтверждения</p>
          {incoming.length === 0 && <p className="text-gray-400 text-center py-6 text-sm">Новых передач нет</p>}
          {incoming.map((i) => (
            <div key={i.id} className="border-t border-gray-100 px-4 py-3 space-y-2">
              <div>
                <p className="font-medium text-sm">{i.partName} — {i.quantity} {i.unit}</p>
                <p className="text-xs text-gray-500">
                  от {i.fromName}{i.fromPoint ? ` · ${i.fromPoint}` : ''}{i.note ? ` · ${i.note}` : ''}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  disabled={confirm.isPending}
                  onClick={() => confirm.mutate(i.id)}
                  className="flex-1 bg-brand-600 text-white text-sm py-2 rounded-lg disabled:opacity-50"
                >
                  Подтвердить
                </button>
                <button
                  disabled={reject.isPending}
                  onClick={() => {
                    const reason = window.prompt('Причина отказа:')
                    if (reason?.trim()) reject.mutate({ id: i.id, reason: reason.trim() })
                  }}
                  className="flex-1 border border-gray-300 text-sm py-2 rounded-lg disabled:opacity-50"
                >
                  Отклонить
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">
          {isOwner ? 'Остатки у механиков' : 'Мой остаток'}
        </p>
        {stock.length === 0 && (
          <p className="text-gray-400 text-center py-6 text-sm">
            {isOwner ? 'Ни у кого нет запчастей под отчётом' : 'Под отчётом ничего нет'}
          </p>
        )}
        {stock.map((s) => (
          <div key={`${s.mechanicId}-${s.partId}`} className="border-t border-gray-100 px-4 py-3 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium">{s.partName}</p>
              {isOwner && <p className="text-xs text-gray-500">{s.mechanicName}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm font-bold">{s.quantity} {s.unit}</span>
              {isOwner && (
                <button onClick={() => onMove(s)} className="text-xs border border-gray-300 px-2 py-1 rounded-lg">
                  Перенести
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Владелец передаёт запчасть механику — уходит «в пути» до подтверждения. */
function HandOverModal({ parts, onClose, onDone }: { parts: PartRow[]; onClose: () => void; onDone: () => void }) {
  const { data: pointsData } = useQuery({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })
  const { data: staffData } = useQuery({ queryKey: ['managers'], queryFn: () => api.get('/users').then((r) => r.data) })
  const [partId, setPartId] = useState('')
  const [fromPointId, setFromPointId] = useState('')
  const [mechanicId, setMechanicId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [note, setNote] = useState('')

  const part = parts.find((p) => p.id === partId)
  const available = availableAt(part, fromPointId)
  const qty = parseFloat(quantity.replace(',', '.'))
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mechanics = ((staffData?.managers ?? []) as any[]).filter((u) => u.role === 'mechanic' || u.roles?.includes('mechanic'))

  const m = useMutation({
    mutationFn: () => api.post('/warehouse/hand-over', { partId, fromPointId, mechanicId, quantity: qty, note: note.trim() || undefined }),
    onSuccess: onDone,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => alert(e?.response?.data?.error?.message ?? 'Ошибка'),
  })

  return (
    <Modal title="Передать механику" onClose={onClose}>
      <select value={partId} onChange={(e) => setPartId(e.target.value)} className="input">
        <option value="">— запчасть —</option>
        {parts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select value={fromPointId} onChange={(e) => setFromPointId(e.target.value)} className="input">
        <option value="">— со склада/точки —</option>
        {(pointsData?.points ?? []).map((p: { id: string; name: string }) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {partId && fromPointId && <p className="text-xs text-gray-500">Доступно: {available} {part?.unit}</p>}
      <select value={mechanicId} onChange={(e) => setMechanicId(e.target.value)} className="input">
        <option value="">— механик —</option>
        {mechanics.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      <input value={quantity} onChange={(e) => setQuantity(e.target.value)} inputMode="decimal" placeholder="Количество (можно дробное, напр. 0.5)" className="input" />
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Комментарий (необязательно)" className="input" />
      <p className="text-xs text-gray-400">Запчасть сразу спишется с точки и будет «в пути», пока механик не подтвердит получение.</p>
      <button
        disabled={!partId || !fromPointId || !mechanicId || !(qty > 0) || qty > available || m.isPending}
        onClick={() => m.mutate()}
        className="w-full bg-brand-600 text-white font-semibold py-3 rounded-xl disabled:opacity-50"
      >
        {m.isPending ? 'Передаю…' : 'Передать'}
      </button>
    </Modal>
  )
}

/** Владелец забирает остаток у механика — на точку или другому механику. Без его подтверждения. */
function MoveFromMechanicModal({ row, onClose, onDone }: { row: MechanicStockRow; onClose: () => void; onDone: () => void }) {
  const { data: pointsData } = useQuery({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })
  const { data: staffData } = useQuery({ queryKey: ['managers'], queryFn: () => api.get('/users').then((r) => r.data) })
  const [target, setTarget] = useState<'point' | 'mechanic'>('point')
  const [toPointId, setToPointId] = useState('')
  const [toMechanicId, setToMechanicId] = useState('')
  const [quantity, setQuantity] = useState(String(row.quantity))

  const qty = parseFloat(quantity.replace(',', '.'))
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mechanics = ((staffData?.managers ?? []) as any[])
    .filter((u) => (u.role === 'mechanic' || u.roles?.includes('mechanic')) && u.id !== row.mechanicId)

  const m = useMutation({
    mutationFn: () => api.post('/warehouse/from-mechanic', {
      partId: row.partId,
      fromMechanicId: row.mechanicId,
      quantity: qty,
      ...(target === 'point' ? { toPointId } : { toMechanicId }),
    }),
    onSuccess: onDone,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => alert(e?.response?.data?.error?.message ?? 'Ошибка'),
  })

  return (
    <Modal title={`Перенести от ${row.mechanicName}`} onClose={onClose}>
      <p className="text-sm">{row.partName} · под отчётом {row.quantity} {row.unit}</p>
      <div className="flex gap-2">
        <button onClick={() => setTarget('point')} className={`flex-1 text-sm py-2 rounded-lg border ${target === 'point' ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600'}`}>На точку</button>
        <button onClick={() => setTarget('mechanic')} className={`flex-1 text-sm py-2 rounded-lg border ${target === 'mechanic' ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600'}`}>Другому механику</button>
      </div>
      {target === 'point' ? (
        <select value={toPointId} onChange={(e) => setToPointId(e.target.value)} className="input">
          <option value="">— точка —</option>
          {(pointsData?.points ?? []).map((p: { id: string; name: string }) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      ) : (
        <select value={toMechanicId} onChange={(e) => setToMechanicId(e.target.value)} className="input">
          <option value="">— механик —</option>
          {mechanics.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      )}
      <input value={quantity} onChange={(e) => setQuantity(e.target.value)} inputMode="decimal" placeholder="Количество" className="input" />
      <p className="text-xs text-gray-400">
        {target === 'point'
          ? 'Подтверждение механика не требуется — запчасть сразу вернётся на точку.'
          : 'Отдающий механик не подтверждает. Получатель увидит передачу и подтвердит её у себя.'}
      </p>
      <button
        disabled={!(qty > 0) || qty > row.quantity || (target === 'point' ? !toPointId : !toMechanicId) || m.isPending}
        onClick={() => m.mutate()}
        className="w-full bg-brand-600 text-white font-semibold py-3 rounded-xl disabled:opacity-50"
      >
        {m.isPending ? 'Переношу…' : 'Перенести'}
      </button>
    </Modal>
  )
}

/** Available quantity of a part at a given point (from the stock breakdown). */
function availableAt(part: PartRow | undefined, pointId: string): number {
  if (!part || !pointId) return 0
  return part.stockByPoint.find((s) => s.pointId === pointId)?.quantity ?? 0
}

function TransferModal({ parts, onClose, onDone }: { parts: PartRow[]; onClose: () => void; onDone: () => void }) {
  const { data: pointsData } = useQuery({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })
  const points: { id: string; name: string }[] = pointsData?.points ?? []
  const [partId, setPartId] = useState('')
  const [fromPointId, setFromPointId] = useState('')
  const [toPointId, setToPointId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const part = parts.find((p) => p.id === partId)
  const avail = availableAt(part, fromPointId)
  const qty = parseFloat(quantity.replace(',', '.'))  // дробные для расходников (0.2 л)
  const canSubmit = !!partId && !!fromPointId && !!toPointId && fromPointId !== toPointId && qty > 0 && qty <= avail

  const m = useMutation({
    mutationFn: () => api.post('/warehouse/transfer', { partId, fromPointId, toPointId, quantity: qty, note: note.trim() || undefined }),
    onSuccess: onDone,
    onError: (e: { response?: { data?: { error?: { message?: string } } } }) => setError(e?.response?.data?.error?.message ?? 'Ошибка'),
  })

  return (
    <Modal title="Перемещение запчастей" onClose={onClose}>
      <label className="block text-xs text-gray-600">Запчасть
        <select value={partId} onChange={(e) => setPartId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
          <option value="">— выберите —</option>
          {parts.map((p) => <option key={p.id} value={p.id}>{p.name} (всего {p.totalQuantity})</option>)}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs text-gray-600">Откуда
          <select value={fromPointId} onChange={(e) => setFromPointId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="">— выберите —</option>
            {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label className="block text-xs text-gray-600">Куда
          <select value={toPointId} onChange={(e) => setToPointId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="">— выберите —</option>
            {points.filter((p) => p.id !== fromPointId).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      </div>
      {partId && fromPointId && <p className="text-xs text-gray-500">Доступно на точке: <strong>{avail}</strong></p>}
      <label className="block text-xs text-gray-600">Количество
        <input inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="напр. 1 или 0.5" className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        <span className="text-[11px] text-gray-400">дробные — через точку или запятую (0.5 = 0,5)</span>
      </label>
      <label className="block text-xs text-gray-600">Заметка
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="(необязательно)" className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <SubmitRow onClose={onClose} disabled={!canSubmit || m.isPending} pending={m.isPending} onSubmit={() => { setError(null); m.mutate() }} label="Переместить" />
    </Modal>
  )
}

function WriteoffModal({ parts, onClose, onDone }: { parts: PartRow[]; onClose: () => void; onDone: () => void }) {
  const { data: pointsData } = useQuery({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })
  const points: { id: string; name: string }[] = pointsData?.points ?? []
  const { data: bikesData } = useQuery({ queryKey: ['bikes'], queryFn: () => api.get('/bikes').then((r) => r.data) })
  const bikes: { id: string; code: string; model?: string | null }[] = bikesData?.bikes ?? []
  const [partId, setPartId] = useState('')
  const [pointId, setPointId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [bikeId, setBikeId] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Списываем из своего остатка «под отчёт» или со склада точки (#под-отчёт).
  const [fromOwnStock, setFromOwnStock] = useState(false)

  const isMechanic = useAuthStore((s) => s.user?.role) === 'mechanic'
  const { data: ownStockData } = useQuery<{ stock: { partId: string; quantity: number }[] }>({
    queryKey: ['mechanic-stock'],
    queryFn: () => api.get('/warehouse/mechanic-stock').then((r) => r.data),
    enabled: isMechanic,
  })
  const ownQty = (ownStockData?.stock ?? []).find((s) => s.partId === partId)?.quantity ?? 0

  const part = parts.find((p) => p.id === partId)
  const avail = fromOwnStock ? ownQty : availableAt(part, pointId)
  // Fractional allowed — consumables can go 0.2 л
  const qty = parseFloat(quantity.replace(',', '.'))
  const canSubmit = !!partId && (fromOwnStock || !!pointId) && qty > 0 && qty <= avail

  const m = useMutation({
    mutationFn: () => api.post('/warehouse/writeoff', {
      partId, quantity: qty,
      ...(fromOwnStock ? { fromOwnStock: true } : { pointId }),
      bikeId: bikeId || undefined,
      note: note.trim() || undefined,
    }),
    onSuccess: onDone,
    onError: (e: { response?: { data?: { error?: { message?: string } } } }) => setError(e?.response?.data?.error?.message ?? 'Ошибка'),
  })

  return (
    <Modal title="Списание запчасти" onClose={onClose}>
      <label className="block text-xs text-gray-600">Запчасть
        <select value={partId} onChange={(e) => setPartId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
          <option value="">— выберите —</option>
          {parts.map((p) => <option key={p.id} value={p.id}>{p.name} (всего {p.totalQuantity})</option>)}
        </select>
      </label>
      {isMechanic && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setFromOwnStock(true)}
            className={`flex-1 text-sm py-2 rounded-lg border ${fromOwnStock ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600'}`}
          >
            Из своего остатка
          </button>
          <button
            type="button"
            onClick={() => setFromOwnStock(false)}
            className={`flex-1 text-sm py-2 rounded-lg border ${!fromOwnStock ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600'}`}
          >
            Со склада точки
          </button>
        </div>
      )}
      {fromOwnStock ? (
        <p className="text-xs text-gray-500">
          Под отчётом у вас: <strong>{ownQty}</strong>{part ? ` ${part.unit}` : ''}
        </p>
      ) : (
        <>
          <label className="block text-xs text-gray-600">Точка (откуда списываем)
            <select value={pointId} onChange={(e) => setPointId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
              <option value="">— выберите —</option>
              {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          {partId && pointId && <p className="text-xs text-gray-500">Доступно на точке: <strong>{avail}</strong></p>}
        </>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs text-gray-600">Количество
          <input inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="напр. 1 или 0.2" className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>
        <label className="block text-xs text-gray-600">Велосипед (необязательно)
          <select value={bikeId} onChange={(e) => setBikeId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="">— без привязки —</option>
            {bikes.map((b) => <option key={b.id} value={b.id}>#{b.code}{b.model ? ` · ${b.model}` : ''}</option>)}
          </select>
        </label>
      </div>
      <label className="block text-xs text-gray-600">Заметка (причина ремонта)
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="(необязательно)" className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <SubmitRow onClose={onClose} disabled={!canSubmit || m.isPending} pending={m.isPending} onSubmit={() => { setError(null); m.mutate() }} label="Списать" />
    </Modal>
  )
}

function NewPartModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('')
  const [unit, setUnit] = useState('шт')
  const [error, setError] = useState<string | null>(null)
  const m = useMutation({
    mutationFn: () => api.post('/warehouse/parts', { name: name.trim(), unit: unit.trim() || 'шт' }),
    onSuccess: onDone,
    onError: (e: { response?: { data?: { error?: { message?: string } } } }) => setError(e?.response?.data?.error?.message ?? 'Не удалось создать запчасть'),
  })
  return (
    <Modal title="Новая запчасть" onClose={onClose}>
      <label className="block text-xs text-gray-600">Название
        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
      </label>
      <label className="block text-xs text-gray-600">Единица
        <input value={unit} onChange={(e) => setUnit(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <SubmitRow onClose={onClose} disabled={name.trim().length < 1 || m.isPending} pending={m.isPending} onSubmit={() => { setError(null); m.mutate() }} label="Создать" />
    </Modal>
  )
}

function ReceiptModal({ parts, onClose, onDone }: { parts: PartRow[]; onClose: () => void; onDone: () => void }) {
  const { data: pointsData } = useQuery({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })
  const points: { id: string; name: string }[] = pointsData?.points ?? []
  const [partId, setPartId] = useState('')
  const [pointId, setPointId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [unitCost, setUnitCost] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const qty = parseFloat(quantity.replace(',', '.'))  // дробные для расходников (0.2 л)
  const cost = parseFloat(unitCost)
  const canSubmit = !!partId && !!pointId && qty > 0 && !isNaN(cost) && cost >= 0

  const m = useMutation({
    mutationFn: () => api.post('/warehouse/receipt', { partId, pointId, quantity: qty, unitCost: cost, note: note.trim() || undefined }),
    onSuccess: onDone,
    onError: (e: { response?: { data?: { error?: { message?: string } } } }) => setError(e?.response?.data?.error?.message ?? 'Ошибка'),
  })

  return (
    <Modal title="Приход запчастей" onClose={onClose}>
      <label className="block text-xs text-gray-600">Запчасть
        <select value={partId} onChange={(e) => setPartId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
          <option value="">— выберите —</option>
          {parts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label className="block text-xs text-gray-600">Точка (куда приходит)
        <select value={pointId} onChange={(e) => setPointId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
          <option value="">— выберите —</option>
          {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs text-gray-600">Количество
          <input inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="напр. 10 или 0.5" className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>
        <label className="block text-xs text-gray-600">Цена за ед., ₽
          <input type="number" min={0} step="0.01" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>
      </div>
      <label className="block text-xs text-gray-600">Заметка
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="(необязательно)" className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <SubmitRow onClose={onClose} disabled={!canSubmit || m.isPending} pending={m.isPending} onSubmit={() => { setError(null); m.mutate() }} label="Оформить приход" />
    </Modal>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function SubmitRow({ onClose, onSubmit, disabled, pending, label }: { onClose: () => void; onSubmit: () => void; disabled: boolean; pending: boolean; label: string }) {
  return (
    <div className="flex gap-2 pt-1">
      <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-gray-300 text-sm">Отмена</button>
      <button onClick={onSubmit} disabled={disabled} className="flex-1 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium disabled:opacity-50">
        {pending ? 'Сохранение…' : label}
      </button>
    </div>
  )
}
